/**
 * Team Panel (Schritt „Benutzer")
 *
 * Tabelle aller Workspace-Benutzer mit ihrer (geplanten) Benutzerlizenz.
 * Neue Benutzer werden über ein Modal hinzugefügt; dort erklärt ein ⓘ pro
 * Lizenz beim Hovern die Unterschiede. Die geplanten Lizenzen speisen die
 * Seat-Mengen im nächsten Schritt („Lizenz") — Mix aus Recruiting und Sales
 * ist möglich.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { API_V1 } from '../../../API/APIs/APIv1';
import { getUserInfo } from '../../../API/user';
import useAIChatStore from '../../../stores/ai-chat-store';
import { dc } from '../design-tokens';

interface TeamPanelProps {
  isAdmin: boolean;
  onSaved: (summary: string) => void;
  /** Ob der Admin den Schritt bestätigt/übersprungen hat — erst dann ist er erledigt. */
  confirmed: boolean;
  onConfirm: () => void;
  /** Schon bestätigt: weiter zum nächsten Schritt. */
  onNext: () => void;
}

interface UserRow {
  id: string;
  name: string;
  email: string;
  role: string;
}

const TeamPanel: React.FC<TeamPanelProps> = ({ isAdmin, onSaved, confirmed, onConfirm, onNext }) => {
  const { classes, cx } = useStyles();
  const { i18n } = useTranslation();
  const isDe = i18n.language === 'de';
  const dataVersion = useAIChatStore((state) => state.dataVersion);

  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal-Zustand
  const [modalOpen, setModalOpen] = useState(false);
  const [ownUserId, setOwnUserId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member');
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await API_V1.get('/tenants/current/users');
        if (cancelled) return;
        const rows: UserRow[] = (res.data?.users || []).map((u: any) => ({
          id: u.id,
          name: [u.firstname, u.lastname].filter(Boolean).join(' ') || u.email,
          email: u.email,
          role: u.role,
        }));
        setUsers(rows);
      } catch {
        if (!cancelled) setUsers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataVersion, isAdmin]);

  // Eigener User: darf sich hier nicht selbst entfernen
  useEffect(() => {
    getUserInfo().then((u) => setOwnUserId(u?._id || u?.id || null));
  }, []);

  const handleRemove = async (u: UserRow) => {
    const question = isDe
      ? `${u.name} (${u.email}) aus dem Workspace entfernen?`
      : `Remove ${u.name} (${u.email}) from the workspace?`;
    if (!window.confirm(question)) return;
    setRemovingId(u.id);
    setRemoveError(null);
    try {
      await API_V1.delete(`/users/${u.id}`);
      setUsers((prev) => prev.filter((row) => row.id !== u.id));
    } catch (e: any) {
      setRemoveError(
        e?.response?.data?.message ||
          (isDe ? 'Benutzer konnte nicht entfernt werden.' : 'Could not remove the user.')
      );
    } finally {
      setRemovingId(null);
    }
  };

  const openModal = () => {
    setInviteEmail('');
    setInviteFirstName('');
    setInviteLastName('');
    setInviteRole('member');
    setError(null);
    setModalOpen(true);
  };

  const canInvite =
    isAdmin && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail.trim()) && !inviting;

  const handleInvite = async () => {
    if (!canInvite) return;
    setInviting(true);
    setError(null);
    try {
      const res = await API_V1.post('/users', {
        email: inviteEmail.trim().toLowerCase(),
        firstName: inviteFirstName.trim() || undefined,
        lastName: inviteLastName.trim() || undefined,
        role: inviteRole,
        language: isDe ? 'de' : 'en',
      });
      const newId = res.data?._id || res.data?.id;
      if (newId) {
        setUsers((prev) => [
          ...prev,
          {
            id: newId,
            name:
              [inviteFirstName.trim(), inviteLastName.trim()].filter(Boolean).join(' ') ||
              inviteEmail.trim(),
            email: inviteEmail.trim(),
            role: inviteRole,
          },
        ]);
      }
      onSaved(
        isDe
          ? `Ich habe den Benutzer ${inviteEmail.trim()} (${inviteRole === 'admin' ? 'Admin' : 'Member'}) eingeladen. Die Lizenz ordne ich im nächsten Schritt zu. Bitte bestätige kurz.`
          : `I invited the user ${inviteEmail.trim()} (${inviteRole}). I will assign the license in the next step. Please confirm briefly.`
      );
      setModalOpen(false);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          (isDe ? 'Einladung fehlgeschlagen — bitte erneut versuchen.' : 'Invitation failed — please try again.')
      );
    } finally {
      setInviting(false);
    }
  };

  if (loading) {
    return (
      <Box className={classes.loading}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box className={classes.card}>
        <Typography variant="body2" color="textSecondary">
          {isDe
            ? 'Benutzer verwaltet ein Workspace-Admin — für dich ist hier nichts zu tun.'
            : 'Users are managed by a workspace admin — nothing to do for you here.'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Box className={cx(classes.gapBanner, confirmed && classes.doneBanner)}>
        <span className={cx(classes.gapDot, confirmed && classes.doneDot)}>{confirmed ? '✓' : '!'}</span>
        <Typography variant="caption" className={cx(classes.gapText, confirmed && classes.doneText)}>
          {!confirmed
            ? isDe
              ? 'Optional: weitere Benutzer für deinen Workspace anlegen — oder überspringen'
              : 'Optional: add more users to your workspace — or skip'
            : isDe
              ? `${users.length} Benutzer im Workspace`
              : `${users.length} user(s) in the workspace`}
        </Typography>
      </Box>

      {/* Benutzer-Tabelle */}
      <Box className={classes.card}>
        <Box className={classes.tableHeader}>
          <Typography variant="caption" className={classes.sectionLabel}>
            {isDe ? 'Benutzer' : 'Users'}
          </Typography>
          <button type="button" className={classes.addButton} onClick={openModal}>
            <PersonAddIcon className={classes.addIcon} />
            {isDe ? 'Benutzer hinzufügen' : 'Add user'}
          </button>
        </Box>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.th}>{isDe ? 'Name' : 'Name'}</TableCell>
              <TableCell className={classes.th}>E-Mail</TableCell>
              <TableCell className={classes.th}>{isDe ? 'Rolle' : 'Role'}</TableCell>
              <TableCell className={classes.th} />
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} className={classes.tr}>
                <TableCell className={classes.tdName}>{u.name}</TableCell>
                <TableCell className={classes.td}>{u.email}</TableCell>
                <TableCell className={classes.td}>{u.role}</TableCell>
                <TableCell className={classes.td} align="right">
                  {u.id !== ownUserId && (
                    <Tooltip title={isDe ? 'Benutzer entfernen' : 'Remove user'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleRemove(u)}
                          disabled={removingId === u.id}
                          aria-label={isDe ? 'Benutzer entfernen' : 'Remove user'}
                        >
                          {removingId === u.id ? <CircularProgress size={14} /> : <DeleteOutlineIcon fontSize="small" />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {removeError && (
          <Typography variant="caption" color="error">
            {removeError}
          </Typography>
        )}
        <Typography variant="caption" color="textSecondary">
          {isDe
            ? 'Welche SAIA-Lizenz jeder Benutzer bekommt, wählst du im nächsten Schritt „Lizenz".'
            : 'You choose which SAIA license each user gets in the next step "License".'}
        </Typography>
      </Box>

      <Box className={classes.confirmRow}>
        <button type="button" className={classes.saveButton} onClick={confirmed ? onNext : onConfirm}>
          {confirmed || users.length > 1 ? (isDe ? 'Weiter' : 'Next') : isDe ? 'Überspringen' : 'Skip'}
        </button>
      </Box>

      {/* Modal: Benutzer erstellen */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle className={classes.modalTitle}>
          {isDe ? 'Benutzer erstellen' : 'Create user'}
          <IconButton size="small" onClick={() => setModalOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.modalContent}>
          <Box className={classes.row}>
            <TextField
              size="small"
              fullWidth
              label={isDe ? 'Vorname' : 'First name'}
              value={inviteFirstName}
              onChange={(e) => setInviteFirstName(e.target.value)}
            />
            <TextField
              size="small"
              fullWidth
              label={isDe ? 'Nachname' : 'Last name'}
              value={inviteLastName}
              onChange={(e) => setInviteLastName(e.target.value)}
            />
          </Box>
          <TextField
            size="small"
            fullWidth
            type="email"
            label={isDe ? 'E-Mail-Adresse' : 'Email address'}
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <TextField
            size="small"
            fullWidth
            select
            label={isDe ? 'Rolle' : 'Role'}
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as 'member' | 'admin')}
          >
            <MenuItem value="member">Member</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </TextField>

          {error && (
            <Typography variant="caption" className={classes.error}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions className={classes.modalActions}>
          <Button onClick={() => setModalOpen(false)} color="inherit">
            {isDe ? 'Abbrechen' : 'Cancel'}
          </Button>
          <button
            type="button"
            className={classes.saveButton}
            onClick={handleInvite}
            disabled={!canInvite}
          >
            {inviting ? (isDe ? 'Lädt ein…' : 'Inviting…') : isDe ? 'Benutzer erstellen' : 'Create user'}
          </button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    maxWidth: 720,
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 32,
  },
  gapBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '9px 14px',
    borderRadius: 12,
    backgroundColor: dc.warnBg,
    border: `1px solid ${dc.warnBorder}`,
  },
  gapDot: {
    flexShrink: 0,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: dc.warn,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    display: 'grid',
    placeItems: 'center',
  },
  gapText: {
    color: dc.warnText,
    lineHeight: 1.4,
  },
  doneBanner: {
    backgroundColor: '#EAFBF4',
    border: '1px solid #C6EEDD',
  },
  doneDot: {
    backgroundColor: dc.success,
  },
  doneText: {
    color: '#0B7050',
  },
  card: {
    backgroundColor: dc.surface,
    border: `1px solid ${dc.border}`,
    borderRadius: 16,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  sectionLabel: {
    fontSize: 11,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: dc.muted,
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    border: `1px solid ${dc.border}`,
    backgroundColor: dc.surface,
    color: dc.primary,
    borderRadius: 10,
    padding: '6px 12px',
    fontFamily: 'inherit',
    fontSize: 12.5,
    fontWeight: 700,
    cursor: 'pointer',
    '&:hover': {
      borderColor: dc.primary,
      backgroundColor: dc.primaryHoverBg,
    },
  },
  addIcon: {
    fontSize: '1rem',
  },
  table: {
    '& .MuiTableCell-root': {
      borderBottom: `1px solid ${dc.borderSoft}`,
    },
  },
  th: {
    fontSize: 11,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: dc.faint,
    padding: '6px 10px 8px',
  },
  tr: {
    '&:hover': {
      backgroundColor: dc.bg,
    },
  },
  td: {
    fontSize: 13,
    color: dc.muted,
    padding: '8px 10px',
  },
  tdName: {
    fontSize: 13,
    fontWeight: 600,
    color: dc.ink,
    padding: '8px 10px',
  },
  modalTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontWeight: 700,
  },
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    paddingTop: '8px !important',
  },
  modalActions: {
    padding: '12px 24px 18px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  error: {
    color: theme.palette.error.main,
  },
  confirmRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  saveButton: {
    flexShrink: 0,
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    backgroundColor: dc.primary,
    borderRadius: 10,
    padding: '9px 18px',
    '&:hover': {
      opacity: 0.9,
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'default',
    },
  },
}));

export default TeamPanel;
